import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, ExportBaselineCheckGQL, ExportBaselineCheckMutation, GetBaselineCheckTreeGQL, GetBaselineCheckTreeQuery, GetRiskMapTreeFiltersGQL, GetRiskMapTreeFiltersQuery, OperatorInput, OrderTypeInput, PagingInput, RiskMapTreeColumns, RiskMapTreeQueryComplexInput, RiskMapTreeSimpleQueryInput, RiskMapTreeSortInput, UpdateBaselineTreeGQL, UpdateBaselineTreeMutation, UpdateTreeNoteBaselineCheckMutation, UpdateTreeNoteBaselineCheckGQL } from "../../../graphql/generated";
import { map, Observable } from "rxjs";
import { MutationData, unwrapMutationData } from "../shared/graphql-mutation-response.util";

@Injectable({
    providedIn: 'root'
})

export class BaselineTreeCheckTreeService {
    private readonly GetBaselineCheckTreeGQL: GetBaselineCheckTreeGQL = inject(GetBaselineCheckTreeGQL);
    private readonly UpdateTreeNoteBaselineCheckGQL: UpdateTreeNoteBaselineCheckGQL = inject(UpdateTreeNoteBaselineCheckGQL);
    private readonly ExportBaselineCheckGQL: ExportBaselineCheckGQL = inject(ExportBaselineCheckGQL);
    private readonly UpdateBaselineTreeGQL: UpdateBaselineTreeGQL = inject(UpdateBaselineTreeGQL);
    private readonly GetRiskMapTreeFiltersGQL: GetRiskMapTreeFiltersGQL = inject(GetRiskMapTreeFiltersGQL)

    getBaselineCheckTree(campaignId: string, legalEntityId: string, parameters: any, au: boolean | null, ics: boolean | null, completed: boolean | null): Observable<GetBaselineCheckTreeQuery['riskMapBaselineTreeByLegalEntityIdAndCampaignId']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetBaselineCheckTreeGQL.fetch({ campaignId, legalEntityId, query: param.query, sort: param.sort, au, ics: ics, completed: completed, page: param.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapBaselineTreeByLegalEntityIdAndCampaignId))
    }

    getAllBaselineCheckTree(
        campaignId: string,
        legalEntityId: string,
        au: boolean | null
    ): Observable<GetRiskMapTreeFiltersQuery['riskMapTreeFields']> {
        return this.GetRiskMapTreeFiltersGQL.fetch({ campaignId, legalEntityId, au: null, active: true }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeFields))
    }

    updateBaelineTree(riskMapId: string[], irRationale: string | null, icsRationale: string | null, filters: boolean): Observable<MutationData<UpdateBaselineTreeMutation['updateBaselineTree']>> {
        return this.UpdateBaselineTreeGQL.mutate({ updateBaseline: { icsRationale, irRationale, riskMapId }, filters }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateBaselineTree), unwrapMutationData('Unable to update the baseline check'))
    }

    updateTreeNote(
        campaignId: string,
        legalEntityId: string,
        processCode: string,
        note: string,
        filter: boolean
    ): Observable<MutationData<UpdateTreeNoteBaselineCheckMutation['updateTreeNote']>> {
        return this.UpdateTreeNoteBaselineCheckGQL.mutate({ campaignId, legalEntityId, processCode, note, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeNote)), unwrapMutationData('Unable to update the note'))
    }

    exportBaselineCheckTree(
        campaignId: string,
        legalEntityId: string,
        timeZone: string,
        parameters: any
    ): Observable<ExportBaselineCheckMutation['downloadBaselineTree']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.ExportBaselineCheckGQL.mutate({ campaignId, legalEntityId, query: param.query, sort: param.sort, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.downloadBaselineTree)))
    }

}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: RiskMapTreeQueryComplexInput, sort: RiskMapTreeSortInput, page: PagingInput } {
    const query: RiskMapTreeSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                if (values === null || !Array.isArray(values) || values.length === 0) return;
                (ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP] || [field]).forEach(key => {
                    let val: string[] = values;
                    if (field === 'icsDeviation' || field === 'irDeviation') {
                        val = val.map(v => String(v) === 'No Match' ? 'true' : 'false');
                    } else if (field === 'au' || field === 'auditableUniverse') {
                        val = val.map(v => String(v) === 'Yes' ? 'true' : 'false');
                    }
                    query.push({
                        field: key as RiskMapTreeColumns,
                        filters: {
                            operator: key === RiskMapTreeColumns.Level ? ComparatorOperators.Eq : ComparatorOperators.Match,
                            value: val
                        }
                    });
                });
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RISKMAP?.[parameters?.order?.sortField as keyof typeof ENUM_RISKMAP]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 3
        }
    }
}

export const ENUM_RISKMAP = {
    name: [RiskMapTreeColumns.Name],
    level: [RiskMapTreeColumns.Level],
    code: [RiskMapTreeColumns.Code],
    cluster: [RiskMapTreeColumns.Cluster],
    note: [RiskMapTreeColumns.Note],
    au: [RiskMapTreeColumns.Au],
    auditableUniverse: [RiskMapTreeColumns.Au],
    riskParameterName: ['RISK_PARAMETER_NAME'],
    icsDeviation: [RiskMapTreeColumns.IcsDeviation],
    irDeviation: [RiskMapTreeColumns.IrDeviation],
    irRationale: [RiskMapTreeColumns.IrRationale],
    icsRationale: [RiskMapTreeColumns.IcsRationale],
    irActual: [RiskMapTreeColumns.IrActual],
    icsActual: [RiskMapTreeColumns.IcsActual],
    irBaseline: [RiskMapTreeColumns.IrBaseline],
    icsBaseline: [RiskMapTreeColumns.IcsBaseline]
}